import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dateUtils, searchForm as searchFormState } from 'shared';

import {
  buildSimplePartialRouteFromMultiRoutes,
  cityCode,
} from '../lib/sync-simple-multi-search';

type MultiCityForm = ReturnType<
  typeof searchFormState.model.useMultiCitySearchForm
>;

export type DirectionValidationLike = {
  dep?: { $error?: boolean };
  arr?: { $error?: boolean };
};

export type SubmitCheckResult = { ok: true } | { ok: false; message: string };

export type ExtraLegErrorCode =
  | 'requiredDep'
  | 'requiredArr'
  | 'isSameDeparture'
  | 'requiredDate'
  | 'dateBeforePrev';

export type ExtraLegError = {
  leg_index: number;
  error_code: ExtraLegErrorCode;
};

/**
 * Encapsulates the "multi-trip" concern of the search form:
 *   - whether the form is currently in multi-trip mode,
 *   - leg manipulation (enter / exit / add / remove),
 *   - per-leg validation surface for the segment rows,
 *   - a single `validateForSubmit` entry point used by the submit pipeline.
 *
 * Owns no underlying form state — it composes the multi-city search-form bag
 * passed in from the orchestrator.
 */
export function useMultiTripMode(deps: {
  searchForm: MultiCityForm;
  baseValidation: Ref<DirectionValidationLike>;
}) {
  const { searchForm, baseValidation } = deps;
  const { t: translate } = useI18n();

  // ---------- State ----------

  /**
   * Derived from the route count so it cannot drift out of sync with the
   * underlying form. Mode-mutators below only touch routes; the flag follows.
   */
  const isMultiTripMode = computed(() => searchForm.routes.value.length > 1);

  /**
   * Latched on submit attempts: extra-leg validation errors are only surfaced
   * once the user has actually tried to submit, mirroring vuelidate's `$dirty`.
   */
  const extraLegSubmitAttempted = ref(false);

  // ---------- Derived for the UI ----------

  const routesList = computed(() => searchForm.routes.value);
  const canAddMoreRoutes = computed(() => searchForm.canAddRoute.value);

  const getDirectionValidation = (
    segmentIndex: number,
  ): DirectionValidationLike => {
    if (segmentIndex === 0) {
      return baseValidation.value;
    }
    const segmentRoute = searchForm.routes.value[segmentIndex];
    if (!segmentRoute || !extraLegSubmitAttempted.value) {
      return { dep: { $error: false }, arr: { $error: false } };
    }
    const depCode = segmentRoute.dep?.code;
    const arrCode = segmentRoute.arr?.code;
    const sameCity =
      depCode && arrCode ? cityCode(depCode) === cityCode(arrCode) : false;
    return {
      dep: { $error: !depCode || sameCity },
      arr: { $error: !arrCode || sameCity },
    };
  };

  // ---------- Mode mutators ----------

  const enterMultiTripMode = () => {
    if (!searchForm.canAddRoute.value) {
      return;
    }
    const firstRoute = searchForm.routes.value[0];
    const firstDate = firstRoute.dates?.[0] ?? dateUtils.getTomorrowDate();
    searchForm.updateRoute({ ...firstRoute, dates: [firstDate] });
    searchForm.addRoute();
    extraLegSubmitAttempted.value = false;
  };

  const exitMultiTripMode = () => {
    const collapsed = buildSimplePartialRouteFromMultiRoutes(
      searchForm.routes.value,
    );
    searchForm.routes.value = searchFormState.lib.buildInitialRoutes([
      collapsed,
    ]);
    extraLegSubmitAttempted.value = false;
  };

  const onAddAnotherLeg = () => {
    if (!searchForm.canAddRoute.value) {
      return;
    }
    searchForm.addRoute();
    extraLegSubmitAttempted.value = false;
  };

  const removeLeg = (routeId: string) => {
    searchForm.removeRoute(routeId);
    extraLegSubmitAttempted.value = false;
  };

  const activateMultiTripSearch = () => {
    if (!isMultiTripMode.value) {
      enterMultiTripMode();
    } else {
      onAddAnotherLeg();
    }
  };

  // ---------- Validation entry-point used by submit pipeline ----------

  /**
   * Walks legs 2..N and returns the first localised error message, or null
   * when every extra leg is valid.
   */
  const findExtraLegError = (): string | null => {
    const routes = searchForm.routes.value;
    for (
      let extraLegIndex = 1;
      extraLegIndex < routes.length;
      extraLegIndex++
    ) {
      const legRoute = routes[extraLegIndex];
      const previousLegDate = routes[extraLegIndex - 1]?.dates?.[0];

      if (!legRoute.dep?.code) {
        return translate('main.validations.requiredDep');
      }
      if (!legRoute.arr?.code) {
        return translate('main.validations.requiredArr');
      }
      if (cityCode(legRoute.dep.code) === cityCode(legRoute.arr.code)) {
        return translate('main.validations.isSameDeparture');
      }
      const departureDate = legRoute.dates?.[0];
      if (!departureDate) {
        return translate('main.multiTrip.requiredDate');
      }
      // Dates are stored as 'YYYY-MM-DD' strings, so lexicographic order
      // matches chronological order — no dayjs needed for this check.
      if (previousLegDate && departureDate < previousLegDate) {
        return translate('main.multiTrip.dateBeforePrev');
      }
    }
    return null;
  };

  /**
   * Single entry-point for the submit pipeline. Also flips
   * `extraLegSubmitAttempted` so segment rows start showing inline errors —
   * that side-effect is intentional and lives here so submit stays ignorant
   * of multi-trip internals.
   */
  const validateForSubmit = (): SubmitCheckResult => {
    if (!isMultiTripMode.value) {
      extraLegSubmitAttempted.value = false;
      return { ok: true };
    }
    const message = findExtraLegError();
    extraLegSubmitAttempted.value = Boolean(message);
    return message ? { ok: false, message } : { ok: true };
  };

  /** Resets the inline-error surface after a non-multi-trip validation fail. */
  const resetExtraLegSubmitAttempted = () => {
    extraLegSubmitAttempted.value = false;
  };

  /**
   * Walks legs 1..N and returns every error found across every extra leg.
   * Unlike {@link findExtraLegError} this does NOT short-circuit — it is
   * meant for analytics, where we want to know how many distinct things
   * were wrong with the multi-trip form on submit.
   */
  const collectExtraLegErrors = (): ExtraLegError[] => {
    const routes = searchForm.routes.value;
    const errors: ExtraLegError[] = [];
    for (
      let extraLegIndex = 1;
      extraLegIndex < routes.length;
      extraLegIndex++
    ) {
      const legRoute = routes[extraLegIndex];
      const previousLegDate = routes[extraLegIndex - 1]?.dates?.[0];
      const depCode = legRoute.dep?.code;
      const arrCode = legRoute.arr?.code;

      if (!depCode) {
        errors.push({ leg_index: extraLegIndex, error_code: 'requiredDep' });
      }
      if (!arrCode) {
        errors.push({ leg_index: extraLegIndex, error_code: 'requiredArr' });
      }
      if (depCode && arrCode && cityCode(depCode) === cityCode(arrCode)) {
        errors.push({
          leg_index: extraLegIndex,
          error_code: 'isSameDeparture',
        });
      }
      const departureDate = legRoute.dates?.[0];
      if (!departureDate) {
        errors.push({ leg_index: extraLegIndex, error_code: 'requiredDate' });
      } else if (previousLegDate && departureDate < previousLegDate) {
        errors.push({ leg_index: extraLegIndex, error_code: 'dateBeforePrev' });
      }
    }
    return errors;
  };

  return {
    isMultiTripMode,
    routesList,
    canAddMoreRoutes,
    getDirectionValidation,

    enterMultiTripMode,
    exitMultiTripMode,
    onAddAnotherLeg,
    removeLeg,
    activateMultiTripSearch,

    validateForSubmit,
    resetExtraLegSubmitAttempted,
    collectExtraLegErrors,
  };
}
